import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
  Alert,
  Stack,
  TextField,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import apiAxiosInstance from '../../utils/axiosInstance';
import { Group } from '../../Types/Group';

type AssignTestDialogProps = {
  open: boolean;
  onClose: () => void;
  testId: string;
  testTitle?: string;
  onAssigned?: (assignments: any[]) => void;
};

const AssignTestDialog: React.FC<AssignTestDialogProps> = ({ open, onClose, testId, testTitle, onAssigned }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [shuffleQuestions, setShuffleQuestions] = useState<boolean>(false);
  const [classCode, setClassCode] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // We no longer use a separate created list; everything flows into existingAssignments
  const [existingAssignments, setExistingAssignments] = useState<any[] | null>(null);
  const [deletingGroups, setDeletingGroups] = useState<Set<string>>(new Set());
  const [creatingGroups, setCreatingGroups] = useState<Set<string>>(new Set());

  // Precompute existing group ids for quick checks
  const existingGroupIdSet = useMemo(() => new Set((existingAssignments || []).map((a: any) => a.groupId)), [existingAssignments]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setExistingAssignments(null);
    setSelectedGroupIds([]);
    setShuffleQuestions(false);
    setClassCode('');

    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const res = await apiAxiosInstance.get<Group[]>('/api/groups');
        setGroups(res.data || []);
      } catch (err: any) {
        console.error('Error loading groups', err);
        setError('Failed to load groups');
      } finally {
        setLoadingGroups(false);
      }
    };
    const fetchExisting = async () => {
      try {
        const res = await apiAxiosInstance.get(`/api/assignments`, { params: { testId, status: 'active' } });
        const assignments = (res.data || []).filter((a: any) => a.status === 'active');
        setExistingAssignments(assignments);
        // Preselect existing group ids so the dropdown reflects assigned groups
        const preselected = assignments.map((a: any) => a.groupId);
        setSelectedGroupIds(preselected);
      } catch (err) {
        // swallow
      }
    };
    fetchGroups();
    fetchExisting();
  }, [open, testId]);

  const handleAssign = async () => {
    try {
      setSubmitting(true);
      setError(null);
      // Only create assignments for groups that do not already have one
      const newGroupIds = selectedGroupIds.filter((id) => !existingGroupIdSet.has(id));
      if (newGroupIds.length === 0) {
        setError('No new groups selected');
        return;
      }
      const payload: any = {
        testId,
        groupIds: newGroupIds,
        options: { shuffleQuestions },
      };
      const res = await apiAxiosInstance.post('/api/assignments', payload);
      const created = Array.isArray(res.data?.assignments) ? res.data.assignments : (Array.isArray(res.data) ? res.data : [res.data]);
      setExistingAssignments(prev => ([...(prev || []), ...created]));
      onAssigned?.(created);
    } catch (err: any) {
      console.error('Assign error', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to assign test';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Utility for building a public link
  const buildPublicLink = (a: any) => `${window.location.origin}/a/${a.publicId}`;

  const handleClose = () => {
    onClose();
  };

  const findGroupName = (groupId: string) => groups.find(g => g._id === groupId)?.name || groupId;

  const handleCopy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const unassignByGroupId = async (groupId: string) => {
    const assignment = (existingAssignments || []).find((a: any) => a.groupId === groupId);
    if (!assignment) return;
    setDeletingGroups(prev => {
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
    try {
      await apiAxiosInstance.delete(`/api/assignments/${assignment._id}`);
      setExistingAssignments(prev => (prev || []).filter((a: any) => a._id !== assignment._id));
    } catch (err) {
      console.error('Unassign failed for group', groupId, err);
      setError('Failed to unassign group');
    } finally {
      setDeletingGroups(prev => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  };

  const assignByGroupId = async (groupId: string) => {
    // Prevent duplicate creates
    if (creatingGroups.has(groupId)) return;
    setCreatingGroups(prev => {
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
    try {
      const payload: any = { testId, groupIds: [groupId], options: { shuffleQuestions } };
      const res = await apiAxiosInstance.post('/api/assignments', payload);
      const created = Array.isArray(res.data?.assignments) ? res.data.assignments : (Array.isArray(res.data) ? res.data : [res.data]);
      // Merge into existing assignments
      setExistingAssignments(prev => ([...(prev || []), ...created]));
      onAssigned?.(created);
    } catch (err: any) {
      console.error('Assign error', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to assign test';
      setError(message);
      // Revert selection for this group on failure
      setSelectedGroupIds(prev => prev.filter(id => id !== groupId));
    } finally {
      setCreatingGroups(prev => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  };

  const handleRotate = async (assignmentId: string) => {
    try {
      const res = await apiAxiosInstance.post(`/api/assignments/${assignmentId}/rotate`);
      const updated = res.data.assignment;
      setExistingAssignments(prev => (prev || []).map((a: any) => a._id === assignmentId ? updated : a));
    } catch (err) {
      console.error('Rotate failed', err);
      setError('Failed to rotate link');
    }
  };

  const handleExpire = async (assignmentId: string) => {
    try {
      const res = await apiAxiosInstance.post(`/api/assignments/${assignmentId}/expire`);
      const updated = res.data.assignment;
      setExistingAssignments(prev => (prev || []).map((a: any) => a._id === assignmentId ? updated : a).filter((a: any) => a.status === 'active'));
    } catch (err) {
      console.error('Expire failed', err);
      setError('Failed to expire assignment');
    }
  };

  const handleRestore = async (assignmentId: string) => {
    try {
      const res = await apiAxiosInstance.post(`/api/assignments/${assignmentId}/restore`);
      const updated = res.data.assignment;
      setExistingAssignments(prev => (prev || []).map((a: any) => a._id === assignmentId ? updated : a));
    } catch (err) {
      console.error('Restore failed', err);
      setError('Failed to restore assignment');
    }
  };

  const handleUnassign = async (assignmentId: string) => {
    try {
      await apiAxiosInstance.delete(`/api/assignments/${assignmentId}`);
      setExistingAssignments(prev => (prev || []).filter((a: any) => a._id !== assignmentId));
    } catch (err) {
      console.error('Unassign failed', err);
      setError('Failed to unassign group');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Group Assignments{testTitle ? `: ${testTitle}` : ''}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Existing assignments */}
        {existingAssignments && existingAssignments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Existing Assignments</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingAssignments.map((a) => {
                  const link = buildPublicLink(a);
                  return (
                    <TableRow key={a._id}>
                      <TableCell>{findGroupName(a.groupId)}</TableCell>
                      <TableCell>
                        <Typography sx={{ wordBreak: 'break-all' }}>{link}</Typography>
                        {a.status !== 'active' && (
                          <Typography variant="caption" color="warning.main">Status: {a.status}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => handleCopy(link)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate link">
                          <span>
                            <IconButton size="small" onClick={() => handleRotate(a._id)} disabled={submitting}>
                              <AutorenewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Expire assignment">
                          <span>
                            <IconButton size="small" color="warning" onClick={() => handleExpire(a._id)} disabled={submitting || a.status === 'expired'}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {a.status !== 'active' && (
                          <Tooltip title="Restore assignment">
                            <span>
                              <Button size="small" onClick={() => handleRestore(a._id)} disabled={submitting}>Restore</Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box>
            {groups.length > 0 ? (
              <>
                <FormControl fullWidth sx={{ mt: 1 }} disabled={loadingGroups}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <InputLabel id="group-select-label">Groups</InputLabel>
                    {selectedGroupIds.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setSelectedGroupIds(Array.from(existingGroupIdSet))}
                      >
                        Clear Selection
                      </Button>
                    )}
                  </Box>
                  <Select
                    labelId="group-select-label"
                    multiple
                    value={selectedGroupIds}
                    onChange={async (e) => {
                      const value = e.target.value as string[];
                      // Handle special values
                      if (value.includes('__all__')) {
                        const allIds = groups.map(g => g._id);
                        // Determine newly added groups compared to current selection
                        const addedIds = allIds.filter(id => !selectedGroupIds.includes(id));
                        setSelectedGroupIds(allIds);
                        if (addedIds.length > 0) {
                          await Promise.all(addedIds
                            .filter(id => !existingGroupIdSet.has(id))
                            .map(id => assignByGroupId(id))
                          );
                        }
                        return;
                      }
                      // Determine adds/removes
                      const removedIds = selectedGroupIds.filter(id => !value.includes(id));
                      const addedIds = value.filter(id => !selectedGroupIds.includes(id));
                      // Optimistically update selection
                      setSelectedGroupIds(value);
                      // Trigger unassign for each removed group
                      if (removedIds.length > 0) {
                        await Promise.all(removedIds.map(id => unassignByGroupId(id)));
                      }
                      // Trigger assign for each added group not already assigned
                      if (addedIds.length > 0) {
                        await Promise.all(addedIds
                          .filter(id => !existingGroupIdSet.has(id))
                          .map(id => assignByGroupId(id))
                        );
                      }
                    }}
                    input={<OutlinedInput label="Groups" />}
                    renderValue={(selected) => {
                      const names = (selected as string[]).map(id => groups.find(g => g._id === id)?.name || id);
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {names.map((name) => (
                            <Chip key={name} label={name} size="small" />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="__all__">
                      <Checkbox 
                        checked={selectedGroupIds.length === groups.length && groups.length > 0} 
                        indeterminate={selectedGroupIds.length > 0 && selectedGroupIds.length < groups.length} 
                      />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {loadingGroups && (
                      <MenuItem value="loading" disabled>
                        <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                      </MenuItem>
                    )}
                    {groups.map((g) => {
                      const isExisting = existingGroupIdSet.has(g._id);
                      const isDeleting = deletingGroups.has(g._id);
                      const isCreating = creatingGroups.has(g._id);
                      return (
                        <MenuItem key={g._id} value={g._id}>
                          <Checkbox checked={selectedGroupIds.indexOf(g._id) > -1} />
                          <ListItemText 
                            primary={g.name} 
                            secondary={isDeleting ? 'Removing…' : (isCreating ? 'Assigning…' : (isExisting ? 'Assigned' : g.description))}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <Stack spacing={2} sx={{ mt: 2 }}>
                  <FormControl>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Checkbox
                        checked={shuffleQuestions}
                        onChange={(e) => setShuffleQuestions(e.target.checked)}
                      />
                      <Typography>Shuffle questions for each student</Typography>
                    </Stack>
                  </FormControl>
                </Stack>
              </>
            ) : (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" paragraph>
                  No groups available to assign to.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create groups in the Roster section to assign tests to specific groups.
                </Typography>
              </Box>
            )}
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTestDialog;


